// src/modules/ambassadors/components/AmbassadorFilters.tsx - Фильтры для списка амбассадоров

import React, { useState } from 'react';
import { Card, Row, Col, Input, Select, DatePicker, InputNumber, Button, Space } from 'antd';
import { SearchOutlined, ClearOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AmbassadorFilters as Filters } from '@/types/models/ambassador';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface AmbassadorFiltersProps {
  onFilter: (filters: Filters) => void;
  loading?: boolean;
}

export const AmbassadorFilters: React.FC<AmbassadorFiltersProps> = ({
  onFilter,
  loading = false,
}) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<boolean | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [clientsRange, setClientsRange] = useState<[number?, number?]>([undefined, undefined]);
  const [salesRange, setSalesRange] = useState<[number?, number?]>([undefined, undefined]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Применить фильтры
  const handleApplyFilters = () => {
    const filters: Filters = {
      search: search || undefined,
      is_active: status,
      date_from: dateRange?.[0],
      date_to: dateRange?.[1],
      min_clients: clientsRange[0],
      max_clients: clientsRange[1],
      min_sales: salesRange[0],
      max_sales: salesRange[1],
    };

    onFilter(filters);
  };

  // Сбросить фильтры
  const handleResetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setDateRange(null);
    setClientsRange([undefined, undefined]);
    setSalesRange([undefined, undefined]);
    
    onFilter({});
  };

  // Обработка изменения диапазона дат
  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD'),
      ]);
    } else {
      setDateRange(null);
    }
  };

  return (
    <Card 
      size="small" 
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined />
          <span>Фильтры</span>
        </Space>
      }
      extra={
        <Button
          type="link"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Скрыть' : 'Показать все'}
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        {/* Основные фильтры (всегда видны) */}
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="Поиск по имени, телефону, промокоду"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleApplyFilters}
            allowClear
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder="Статус"
            style={{ width: '100%' }}
            value={status}
            onChange={setStatus}
            allowClear
          >
            <Option value={true}>Активные</Option>
            <Option value={false}>Неактивные</Option>
          </Select>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleApplyFilters}
              loading={loading}
            >
              Применить
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleResetFilters}
            >
              Сбросить
            </Button>
          </Space>
        </Col>

        {/* Расширенные фильтры */}
        {isExpanded && (
          <>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: '100%' }}
                placeholder={['Дата от', 'Дата до']}
                value={
                  dateRange
                    ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                    : null
                }
                onChange={handleDateRangeChange}
                format="DD.MM.YYYY"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="Клиентов от"
                  style={{ width: '50%' }}
                  min={0}
                  value={clientsRange[0]}
                  onChange={(val) => setClientsRange([val || undefined, clientsRange[1]])}
                />
                <InputNumber
                  placeholder="Клиентов до"
                  style={{ width: '50%' }}
                  min={0}
                  value={clientsRange[1]}
                  onChange={(val) => setClientsRange([clientsRange[0], val || undefined])}
                />
              </Space.Compact>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="Продажи от (₸)"
                  style={{ width: '50%' }}
                  min={0}
                  value={salesRange[0]}
                  onChange={(val) => setSalesRange([val || undefined, salesRange[1]])}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => Number(value!.replace(/\s/g, ''))}
                />
                <InputNumber
                  placeholder="Продажи до (₸)"
                  style={{ width: '50%' }}
                  min={0}
                  value={salesRange[1]}
                  onChange={(val) => setSalesRange([salesRange[0], val || undefined])}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => Number(value!.replace(/\s/g, ''))}
                />
              </Space.Compact>
            </Col>
          </>
        )}
      </Row>
    </Card>
  );
};